import React, { useState, useEffect } from "react";
import axios from "axios";
import { getUserData } from "../utils/secureStroge";

function ContactUs() {
  const [accessToken, setAccessToken] = useState(null);

  const refreshAccessToken = async () => {
    try {
      const authData = getUserData();

      const res = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/auth/refresh-token`,
        {
          refreshToken: authData.refreshToken,
        },
      );

      const newAccessToken = res.data.accessToken;

      // update localStorage
      const updatedData = {
        ...authData,
        accessToken: newAccessToken,
      };

      localStorage.setItem("authData", JSON.stringify(updatedData));

      return newAccessToken;
    } catch (err) {
      console.log("Refresh token failed", err);

      // logout
      localStorage.clear();
      window.location.href = "/login";
    }
  };

  useEffect(() => {
    const authData = getUserData();

    if (authData?.accessToken) {
      setAccessToken(authData.accessToken);
    } else {
      setError("User not logged in");
    }
  }, []);

  // Profile state matching API schema
  const [profile, setProfile] = useState({
    full_name: "",
    phone_number: "",
    email: "",
    state: "",
    country: "",
  });
  const [originalProfile, setOriginalProfile] = useState({}); // Original data to compare changes

  // Password change state
  const [changePasswordForm, setChangePasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordVisible, setPasswordVisible] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  console.log("TOKEN:", accessToken);

  // Loading & feedback states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

  // Auto hide messages after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchProfile = async () => {
    if (!accessToken) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/auth/profile`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const profileData = {
        full_name: response.data.full_name || "",
        phone_number: response.data.phone_number || "",
        email: response.data.email || "",
        state: response.data.state || "",
        country: response.data.country || "",
      };

      setProfile(profileData);
      setOriginalProfile(profileData); // Store original data for comparison
    } catch (err) {
      if (err.response?.status === 401) {
        const newToken = await refreshAccessToken();

        if (newToken) {
          setAccessToken(newToken);

          // retry API
          const retryRes = await axios.get(`${API_BASE_URL}/auth/profile`, {
            headers: {
              Authorization: `Bearer ${newToken}`,
            },
          });

          const profileData = {
            full_name: retryRes.data.full_name || "",
            phone_number: retryRes.data.phone_number || "",
            email: retryRes.data.email || "",
            state: retryRes.data.state || "",
            country: retryRes.data.country || "",
          };

          setProfile(profileData);
          setOriginalProfile(profileData);
        }
      } else {
        setError(err.response?.data?.message || "Failed to load profile data");
      }
    }
  };

  // Fetch profile on component mount
  useEffect(() => {
    if (accessToken) {
      fetchProfile();
    }
  }, [accessToken]);

  console.log(accessToken);

  const togglePassword = (field) => {
    setPasswordVisible((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Handle profile form inputs (fixed field names to match API)
  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  // Handle password form inputs
  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setChangePasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  // Check if profile has changes (excluding email)
  const hasProfileChanges = () => {
    const editableFields = ["full_name", "phone_number", "state", "country"];
    return editableFields.some(
      (field) => profile[field]?.trim() !== originalProfile[field]?.trim(),
    );
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    // 🔥 Check if there are actual changes (excluding email)
    if (!hasProfileChanges()) {
      setError("No changes detected. Please modify your profile first.");
      return;
    }

    if (!accessToken) {
      setError("User not authenticated");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      // 🔹 First attempt
      await axios.put(`${API_BASE_URL}/auth/profile`, profile, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Update original profile after successful update
      setOriginalProfile({ ...profile });
      setSuccess("Profile updated successfully!");
    } catch (err) {
      // 🔥 अगर token expire (401)
      if (err.response?.status === 401) {
        try {
          const authData = getUserData();

          // 🔹 refresh token call
          const refreshRes = await axios.post(
            `${API_BASE_URL}/auth/refresh-token`,
            {
              refreshToken: authData.refreshToken,
            },
          );

          const newAccessToken = refreshRes.data.accessToken;

          // 🔹 save new token
          const updatedData = {
            ...authData,
            accessToken: newAccessToken,
          };

          localStorage.setItem("authData", JSON.stringify(updatedData));
          setAccessToken(newAccessToken);

          // 🔹 retry original request
          await axios.put(`${API_BASE_URL}/auth/profile`, profile, {
            headers: {
              Authorization: `Bearer ${newAccessToken}`,
            },
          });

          // Update original profile after successful update
          setOriginalProfile({ ...profile });
          setSuccess("Profile updated successfully!");
        } catch (refreshErr) {
          console.log("Refresh failed:", refreshErr);

          setError("Session expired. Please login again.");

          // logout
          localStorage.clear();
          window.location.href = "/login";
        }
      } else {
        setError(err.response?.data?.message || "Failed to update profile");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      setError("New passwords do not match!");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      await axios.post(
        `${API_BASE_URL}/auth/change-password`,
        {
          oldPassword: changePasswordForm.oldPassword,
          newPassword: changePasswordForm.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      setSuccess("Password changed successfully!");

      // Clear form after success
      setChangePasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      // 🔥 401 → refresh token
      if (err.response?.status === 401) {
        const newToken = await refreshAccessToken();

        if (newToken) {
          setAccessToken(newToken);

          // retry request
          await axios.post(
            `${API_BASE_URL}/auth/change-password`,
            {
              oldPassword: changePasswordForm.oldPassword,
              newPassword: changePasswordForm.newPassword,
            },
            {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            },
          );

          setSuccess("Password changed successfully!");

          // Clear form after success
          setChangePasswordForm({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        }
      } else {
        setError(err.response?.data?.message || "Failed to change password");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main>
      {/* Breadcrumb Section */}
      <section className="page_breadcrumb" data-aos="fade-up">
        <div className="container-fluid">
          <div className="breadcrumb_content centred">
            <h1 className="breadcrumb_title">User Profile</h1>
            <ul className="breadcrumb_menu">
              <li>
                <a href="/">Home</a>
              </li>
              <li>/</li>
              <li>User Profile</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Service Style One */}
      <section
        className="container service_style_one pt_150 mt-5"
        data-aos="fade-up"
        data-aos-duration="500"
      >
        <div className="container-fluid">
          {/* Success/Error Messages */}
          {error && <div className="alert alert-danger mb-4">{error}</div>}
          {success && <div className="alert alert-success mb-4">{success}</div>}

          {/* Tabs */}
          <div className="nav nav-tabs">
            <div className="nav-link active" data-bs-toggle="tab" href="#tab1">
              Profile
            </div>
            <div className="nav-link" data-bs-toggle="tab" href="#tab2">
              Change Password
            </div>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Tab 1 - Profile */}
            <div className="tab-pane fade show active" id="tab1">
              <div className="row align-items-center">
                <div className="col-lg-12 content_column">
                  <h3>User Profile</h3>
                  <form
                    className="contact_form"
                    style={{ maxWidth: "100%" }}
                    onSubmit={handleProfileSubmit}
                  >
                    <div className="row">
                      {/* Full Name */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group">
                          <input
                            type="text"
                            name="full_name"
                            placeholder="Full Name"
                            value={profile.full_name}
                            onChange={handleProfileInputChange}
                            required
                          />
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group">
                          <input
                            type="tel"
                            name="phone_number"
                            placeholder="Phone Number"
                            value={profile.phone_number}
                            onChange={handleProfileInputChange}
                            required
                          />
                        </div>
                      </div>

                      {/* Email - READ ONLY */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group">
                          <input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={profile.email}
                            readOnly
                            style={{
                              backgroundColor: "#f8f9fa",
                              cursor: "not-allowed",
                              opacity: 0.7,
                            }}
                          />
                        </div>
                      </div>

                      {/* State */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group custom-select-wrapper">
                          <select
                            name="state"
                            className="custom-select"
                            value={profile.state}
                            onChange={handleProfileInputChange}
                          >
                            <option value="">Select State</option>
                            <option value="Maharashtra">Maharashtra</option>
                            <option value="Madhya Pradesh">
                              Madhya Pradesh
                            </option>
                            <option value="Gujarat">Gujarat</option>
                          </select>
                          <span className="custom-arrow"></span>
                        </div>
                      </div>

                      {/* Country */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group custom-select-wrapper">
                          <select
                            name="country"
                            className="custom-select"
                            value={profile.country}
                            onChange={handleProfileInputChange}
                          >
                            <option value="">Select Country</option>
                            <option value="India">India</option>
                            <option value="USA">USA</option>
                            <option value="UK">UK</option>
                          </select>
                          <span className="custom-arrow"></span>
                        </div>
                      </div>

                      {/* Submit */}
                      <div className="col-xl-12">
                        <div className="form-group centred">
                          <button
                            type="submit"
                            className="btn_style_one"
                            disabled={isLoading || !hasProfileChanges()}
                          >
                            {isLoading ? "Saving..." : "Save Profile"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Tab 2 - Change Password */}
            <div className="tab-pane fade" id="tab2">
              <div className="row align-items-center">
                <div className="col-lg-12 content_column">
                  <h3>Change Password</h3>
                  <form
                    className="contact_form"
                    style={{ maxWidth: "100%" }}
                    onSubmit={handlePasswordSubmit}
                  >
                    <div className="row">
                      {/* Current Password */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group position-relative">
                          <input
                            type={passwordVisible.current ? "text" : "password"}
                            name="oldPassword"
                            placeholder="Current Password"
                            value={changePasswordForm.oldPassword}
                            onChange={handlePasswordInputChange}
                            required
                          />
                          <span
                            className="toggle-password"
                            onClick={() => togglePassword("current")}
                          >
                            <i
                              className={`fa ${passwordVisible.current ? "fa-eye-slash" : "fa-eye"}`}
                            ></i>
                          </span>
                        </div>
                      </div>

                      {/* New Password */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group position-relative">
                          <input
                            type={passwordVisible.new ? "text" : "password"}
                            name="newPassword"
                            placeholder="New Password"
                            value={changePasswordForm.newPassword}
                            onChange={handlePasswordInputChange}
                            required
                          />
                          <span
                            className="toggle-password"
                            onClick={() => togglePassword("new")}
                          >
                            <i
                              className={`fa ${passwordVisible.new ? "fa-eye-slash" : "fa-eye"}`}
                            ></i>
                          </span>
                        </div>
                      </div>

                      {/* Confirm New Password */}
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <div className="form-group position-relative">
                          <input
                            type={passwordVisible.confirm ? "text" : "password"}
                            name="confirmPassword"
                            placeholder="Confirm New Password"
                            value={changePasswordForm.confirmPassword}
                            onChange={handlePasswordInputChange}
                            required
                          />
                          <span
                            className="toggle-password"
                            onClick={() => togglePassword("confirm")}
                          >
                            <i
                              className={`fa ${passwordVisible.confirm ? "fa-eye-slash" : "fa-eye"}`}
                            ></i>
                          </span>
                        </div>
                      </div>

                      {/* Submit */}
                      <div className="col-xl-12">
                        <div className="form-group centred">
                          <button
                            type="submit"
                            className="btn_style_one"
                            disabled={isLoading}
                          >
                            {isLoading ? "Updating..." : "Update Password"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ContactUs;
